import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { getDomains, getPriceRules, getAllPriceRules, getSyncTemplates } from '@/services/api';
import { Button } from '@/components/ui/button';
import {
    Plus, DollarSign, TrendingUp, TrendingDown, Minus,
    RefreshCw, Settings, Pencil, Filter, Globe, Zap,
    ChevronDown, Check, Search, X, BarChart3
} from 'lucide-react';
import type { PriceRule } from '@/types/price-rules';
import type { Domain } from '@/types/domain';
import DeleteRuleDialog from './components/DeleteRuleDialog';
import SyncRuleModal from './components/SyncRuleModal';
import RuleReportModal from './components/RuleReportModal';
import EditRuleModal from './components/EditRuleModal';

export default function PriceRules() {
    const navigate = useNavigate();
    const [selectedDomainId, setSelectedDomainId] = useState<number | null>(null);
    const [domainOpen, setDomainOpen] = useState(false);
    const [domainSearch, setDomainSearch] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [filters, setFilters] = useState({ country: '', device: '', status: '' });
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [syncModalOpen, setSyncModalOpen] = useState(false);
    const [reportModalOpen, setReportModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [selectedRule, setSelectedRule] = useState<PriceRule | null>(null);

    const { data: domains, isLoading: domainsLoading } = useQuery({
        queryKey: ['domains'],
        queryFn: getDomains,
    });

    const { data: rules, isLoading: rulesLoading, refetch: refetchRules } = useQuery({
        queryKey: ['price-rules', selectedDomainId],
        queryFn: () => selectedDomainId ? getPriceRules(selectedDomainId) : getAllPriceRules(),
    });

    const { data: templates } = useQuery({
        queryKey: ['sync-templates'],
        queryFn: () => getSyncTemplates(),
    });

    const filteredDomains = domains?.filter((d: Domain) =>
        d.domain.toLowerCase().includes(domainSearch.toLowerCase())
    ) || [];

    const filteredRules = rules?.filter((rule) => {
        if (filters.country && rule.country !== filters.country) return false;
        if (filters.device && rule.device !== filters.device) return false;
        if (filters.status && rule.status !== filters.status) return false;
        return true;
    });

    const uniqueCountries = [...new Set(rules?.map(r => r.country) || [])];
    const activeFiltersCount = Object.values(filters).filter(Boolean).length;

    const selectedDomain = domains?.find((d: Domain) => d.id === selectedDomainId);

    const getFaviconUrl = (domain: string) => `https://www.google.com/s2/favicons?domain=${domain}&sz=32`;

    const getDomainName = (domainId: number) => {
        const domain = domains?.find((d: Domain) => d.id === domainId);
        return domain?.domain || `ID: ${domainId}`;
    };

    const formatPrice = (price: number) =>
        new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'USD' }).format(price);

    const getTemplateName = (templateId: number | null) => {
        if (!templateId || !templates) return '-';
        return templates.find(t => t.id === templateId)?.name || '-';
    };

    const getPmrStatus = (currentPmr: number | null, pmrTarget: number) => {
        if (currentPmr === null) return { icon: null, color: 'text-gray-400', bg: 'bg-gray-100' };
        const diff = currentPmr - pmrTarget;
        if (diff >= 5) return { icon: <TrendingUp className="h-4 w-4" />, color: 'text-emerald-600', bg: 'bg-emerald-100' };
        if (diff <= -5) return { icon: <TrendingDown className="h-4 w-4" />, color: 'text-red-600', bg: 'bg-red-100' };
        return { icon: <Minus className="h-4 w-4" />, color: 'text-gray-500', bg: 'bg-gray-100' };
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Price Rules</h1>
                    <p className="mt-1 text-sm text-gray-500">Gerencie as regras de preço para otimização automática</p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={() => navigate('/price-rules/templates')} className="h-10">
                        <Settings className="mr-2 h-4 w-4" />
                        Templates
                    </Button>
                    <Button variant="outline" onClick={() => navigate('/price-rules/create-bulk')} className="h-10">
                        <Zap className="mr-2 h-4 w-4" />
                        Criar em Massa
                    </Button>
                    <Button onClick={() => navigate('/price-rules/create')} className="h-10 bg-blue-600 hover:bg-blue-700">
                        <Plus className="mr-2 h-4 w-4" />
                        Nova Regra
                    </Button>
                </div>
            </div>

            {/* Filters Bar */}
            <div className="flex items-center gap-3 p-4 bg-white rounded-xl border border-gray-200">
                {/* Domain Select */}
                <div className="relative flex-1 max-w-xs">
                    <button
                        type="button"
                        onClick={() => setDomainOpen(!domainOpen)}
                        className="w-full h-10 px-4 text-left text-sm font-medium border-2 border-gray-200 rounded-xl bg-white flex items-center justify-between hover:border-gray-300 focus:outline-none focus:border-blue-500"
                    >
                        {selectedDomain ? (
                            <div className="flex items-center gap-2">
                                <img src={getFaviconUrl(selectedDomain.domain)} alt="" className="w-4 h-4 rounded" />
                                <span className="truncate">{selectedDomain.domain}</span>
                            </div>
                        ) : (
                            <div className="flex items-center gap-2">
                                <Globe className="h-4 w-4 text-gray-400" />
                                <span className="text-gray-500">Todos os domínios</span>
                            </div>
                        )}
                        <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${domainOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {domainOpen && (
                        <>
                            <div className="fixed inset-0 z-10" onClick={() => { setDomainOpen(false); setDomainSearch(''); }} />
                            <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border-2 border-gray-200 rounded-xl shadow-lg overflow-hidden">
                                <div className="p-2 border-b border-gray-100">
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                        <input
                                            type="text"
                                            placeholder="Buscar domínio..."
                                            value={domainSearch}
                                            onChange={(e) => setDomainSearch(e.target.value)}
                                            className="w-full h-9 pl-9 pr-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-500"
                                            onClick={(e) => e.stopPropagation()}
                                            autoFocus
                                        />
                                    </div>
                                </div>
                                <div className="max-h-[240px] overflow-y-auto">
                                    <button
                                        type="button"
                                        onClick={() => { setSelectedDomainId(null); setDomainOpen(false); setDomainSearch(''); }}
                                        className={`w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 flex items-center justify-between ${
                                            !selectedDomainId ? 'bg-blue-50 text-blue-700 font-medium' : ''
                                        }`}
                                    >
                                        <div className="flex items-center gap-2">
                                            <Globe className="h-4 w-4" />
                                            Todos os domínios
                                        </div>
                                        {!selectedDomainId && <Check className="h-4 w-4" />}
                                    </button>
                                    {filteredDomains.map((domain: Domain) => (
                                        <button
                                            key={domain.id}
                                            type="button"
                                            onClick={() => { setSelectedDomainId(domain.id); setDomainOpen(false); setDomainSearch(''); }}
                                            className={`w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 flex items-center justify-between ${
                                                selectedDomainId === domain.id ? 'bg-blue-50 text-blue-700 font-medium' : ''
                                            }`}
                                        >
                                            <div className="flex items-center gap-2">
                                                <img src={getFaviconUrl(domain.domain)} alt="" className="w-4 h-4 rounded" />
                                                {domain.domain}
                                            </div>
                                            {selectedDomainId === domain.id && <Check className="h-4 w-4" />}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}
                </div>

                <div className="h-6 w-px bg-gray-200" />

                {/* Quick Filters */}
                <Button
                    variant={showFilters ? "default" : "outline"}
                    size="sm"
                    onClick={() => setShowFilters(!showFilters)}
                    className="h-10"
                >
                    <Filter className="mr-2 h-4 w-4" />
                    Filtros
                    {activeFiltersCount > 0 && (
                        <span className="ml-2 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">
                            {activeFiltersCount}
                        </span>
                    )}
                </Button>

                <Button variant="ghost" size="sm" onClick={() => refetchRules()} className="h-10 px-3">
                    <RefreshCw className="h-4 w-4" />
                </Button>

                <div className="flex-1" />

                {/* Count */}
                <span className="text-sm text-gray-500">
                    {filteredRules?.length || 0} regra{(filteredRules?.length || 0) !== 1 ? 's' : ''}
                </span>
            </div>

            {/* Expanded Filters */}
            {showFilters && (
                <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 flex items-center gap-4">
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-500">País:</span>
                        <select
                            value={filters.country}
                            onChange={(e) => setFilters({ ...filters, country: e.target.value })}
                            className="h-9 px-3 text-sm border border-gray-200 rounded-lg bg-white"
                        >
                            <option value="">Todos</option>
                            {uniqueCountries.map(c => (
                                <option key={c} value={c}>{c === '*' ? 'Todos países' : c}</option>
                            ))}
                        </select>
                    </div>

                    <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-500">Device:</span>
                        <select
                            value={filters.device}
                            onChange={(e) => setFilters({ ...filters, device: e.target.value })}
                            className="h-9 px-3 text-sm border border-gray-200 rounded-lg bg-white"
                        >
                            <option value="">Todos</option>
                            <option value="*">Todos devices</option>
                            <option value="MOBILE">Mobile</option>
                            <option value="WEB">Web</option>
                        </select>
                    </div>

                    <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-500">Status:</span>
                        <select
                            value={filters.status}
                            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                            className="h-9 px-3 text-sm border border-gray-200 rounded-lg bg-white"
                        >
                            <option value="">Todos</option>
                            <option value="active">Ativo</option>
                            <option value="inactive">Inativo</option>
                            <option value="paused">Pausado</option>
                        </select>
                    </div>

                    {activeFiltersCount > 0 && (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setFilters({ country: '', device: '', status: '' })}
                            className="text-gray-500"
                        >
                            <X className="mr-1 h-3 w-3" />
                            Limpar
                        </Button>
                    )}
                </div>
            )}

            {/* Loading */}
            {(domainsLoading || rulesLoading) && (
                <div className="flex items-center justify-center py-12">
                    <div className="flex items-center gap-3">
                        <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                        <span className="text-gray-600">Carregando...</span>
                    </div>
                </div>
            )}

            {/* Empty State */}
            {!rulesLoading && filteredRules?.length === 0 && (
                <div className="flex flex-col items-center justify-center py-16 bg-white rounded-xl border-2 border-dashed border-gray-200">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                        <DollarSign className="h-8 w-8 text-gray-400" />
                    </div>
                    <h3 className="mt-4 text-lg font-semibold text-gray-900">Nenhuma regra encontrada</h3>
                    <p className="mt-1 text-sm text-gray-500">
                        {rules?.length === 0 ? 'Crie sua primeira regra de preço.' : 'Nenhuma regra corresponde aos filtros.'}
                    </p>
                    {rules?.length === 0 && (
                        <Button className="mt-4" onClick={() => navigate('/price-rules/create')}>
                            <Plus className="mr-2 h-4 w-4" />
                            Criar Regra
                        </Button>
                    )}
                </div>
            )}

            {/* Rules Table */}
            {filteredRules && filteredRules.length > 0 && (
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <table className="min-w-full">
                        <thead>
                        <tr className="bg-gray-50 border-b border-gray-200">
                            {!selectedDomainId && (
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Domínio</th>
                            )}
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Segmentação</th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Price</th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">PMR</th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">eCPM</th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Template</th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                            <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Ações</th>
                        </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                        {filteredRules.map((rule) => {
                            const pmrStatus = getPmrStatus(rule.current_pmr, rule.pmr_target);
                            return (
                                <tr key={rule.id} className="hover:bg-gray-50 transition-colors">
                                    {!selectedDomainId && (
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-2">
                                                <img src={getFaviconUrl(getDomainName(rule.domain_id))} alt="" className="w-4 h-4 rounded" />
                                                <span className="text-sm font-medium text-gray-900">{getDomainName(rule.domain_id)}</span>
                                            </div>
                                        </td>
                                    )}
                                    <td className="px-6 py-4">
                                        <div className="space-y-1">
                                            <div className="flex items-center gap-2 text-sm">
                                                    <span className="font-medium text-gray-900">
                                                        {rule.country === '*' ? '🌍 Todos' : rule.country}
                                                    </span>
                                                <span className="text-gray-300">•</span>
                                                <span className="text-gray-600">
                                                        {rule.ad_unit === '*' ? 'Todos Blocos' : rule.ad_unit}
                                                    </span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${
                                                        rule.device === 'MOBILE' ? 'bg-blue-50 text-blue-700' :
                                                            rule.device === 'WEB' ? 'bg-purple-50 text-purple-700' :
                                                                'bg-gray-100 text-gray-600'
                                                    }`}>
                                                        {rule.device === 'MOBILE' ? '📱 Mobile' : rule.device === 'WEB' ? '💻 Web' : '🌐 Todos'}
                                                    </span>
                                                {rule.custom_key && (
                                                    <span
                                                        className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-orange-50 text-orange-700 max-w-[200px]"
                                                        title={rule.custom_key === 'id_post_wp' && rule.slug ? `#${rule.custom_value} /${rule.slug}` : `${rule.custom_key}=${rule.custom_value}`}
                                                    >
                                                            {rule.custom_key === 'id_post_wp' ? (
                                                                <span className="truncate">#{rule.custom_value}{rule.slug && <span className="text-orange-400"> /{rule.slug}</span>}</span>
                                                            ) : (
                                                                <>{rule.custom_key}={rule.custom_value}</>
                                                            )}
                                                        </span>
                                                )}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className="text-sm font-bold text-gray-900">{formatPrice(rule.price)}</span>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-2">
                                            <div className={`p-1 rounded ${pmrStatus.bg}`}>
                                                <span className={pmrStatus.color}>{pmrStatus.icon}</span>
                                            </div>
                                            <div>
                                                    <span className="text-sm font-medium text-gray-900">
                                                        {rule.current_pmr !== null ? `${rule.current_pmr.toFixed(1)}%` : '-'}
                                                    </span>
                                                <span className="text-xs text-gray-400 ml-1">/ {rule.pmr_target}%</span>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                            <span className="text-sm text-gray-600">
                                                {rule.current_ecpm ? formatPrice(rule.current_ecpm) : '-'}
                                            </span>
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className="text-sm text-gray-600">{getTemplateName(rule.template_id)}</span>
                                    </td>
                                    <td className="px-6 py-4">
                                            <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full ${
                                                rule.status === 'active' ? 'bg-emerald-50 text-emerald-700' :
                                                    rule.status === 'paused' ? 'bg-yellow-50 text-yellow-700' :
                                                        'bg-red-50 text-red-700'
                                            }`}>
                                                {rule.status === 'active' ? '● Ativo' : rule.status === 'paused' ? '● Pausado' : '● Inativo'}
                                            </span>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center justify-end gap-1">
                                            <button
                                                onClick={() => { setSelectedRule(rule); setSyncModalOpen(true); }}
                                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                title="Sincronizar"
                                            >
                                                <Zap className="h-4 w-4" />
                                            </button>
                                            <button
                                                onClick={() => { setSelectedRule(rule); setReportModalOpen(true); }}
                                                className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                                                title="Relatório"
                                            >
                                                <BarChart3 className="h-4 w-4" />
                                            </button>
                                            <button
                                                onClick={() => { setSelectedRule(rule); setEditModalOpen(true); }}
                                                className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
                                                title="Editar"
                                            >
                                                <Pencil className="h-4 w-4" />
                                            </button>
                                            {/*<button*/}
                                            {/*    onClick={() => { setSelectedRule(rule); setDeleteDialogOpen(true); }}*/}
                                            {/*    className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"*/}
                                            {/*    title="Excluir"*/}
                                            {/*>*/}
                                            {/*    <Trash2 className="h-4 w-4" />*/}
                                            {/*</button>*/}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                        </tbody>
                    </table>
                </div>
            )}

            <DeleteRuleDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen} rule={selectedRule} onSuccess={() => refetchRules()} />
            <SyncRuleModal open={syncModalOpen} onOpenChange={setSyncModalOpen} rule={selectedRule} onSuccess={() => refetchRules()} />
            <RuleReportModal open={reportModalOpen} onOpenChange={setReportModalOpen} rule={selectedRule} />
            <EditRuleModal open={editModalOpen} onOpenChange={setEditModalOpen} rule={selectedRule} onSuccess={() => refetchRules()} />
        </div>
    );
}